import * as bsl from 'body-scroll-lock';
import * as React from 'react';
import { getSystemLogoHeight, renderSystemLogo } from '../../Header.systems';
import { DropdownType, LinkItemModel, NavigationItemModel } from '../../Header.types';
import S from './MobileNavigation.styles';

export type NavigationMobileProps = {
  isOpen: boolean;
  items: NavigationItemModel[];
  links: LinkItemModel[];
  translations: Record<string, string>;
};

const NavigationMobile: React.FC<NavigationMobileProps> = ({ isOpen, items, links, translations }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [previousItems, setPreviousItems] = React.useState<Array<NavigationItemModel | undefined>>([]);

  React.useEffect(() => {
    if (!ref.current) {
      return;
    }

    const element = ref.current;

    if (isOpen) {
      bsl.disableBodyScroll(element);
    } else {
      bsl.enableBodyScroll(element);
    }

    return () => {
      bsl.enableBodyScroll(element);
      setPreviousItems([]);
    };
  }, [isOpen]);

  const currentParentItem = previousItems?.[previousItems.length - 1];
  const currentItemList = currentParentItem?.children ?? items;
  // const currentChildrenHasChildren = currentParentItem?.children?.find((x) => x.children);

  return (
    <S.Container ref={ref} aria-label="Mobile Navigation" aria-hidden={!isOpen}>
      <S.ItemList>
        {/* Display the back link and parent link, only if there is a parent item */}
        {currentParentItem && (
          <React.Fragment>
            <S.Item>
              <S.BackLink
                aria-label="Back to previous navigation level"
                onClick={(event) => {
                  event.preventDefault();
                  setPreviousItems([...previousItems.slice(0, previousItems.length - 1)]);
                }}
              >
                <S.LeftArrow />
                {translations['custom.back']}
              </S.BackLink>
            </S.Item>
            <S.Item>
              <S.ParentLink
                style={
                  currentParentItem.systemLogo
                    ? ({
                        '--systemLogoHeight': `${getSystemLogoHeight(currentParentItem.systemLogo) * 0.7}px`,
                      } as React.CSSProperties)
                    : {}
                }
              >
                {renderParentLink()}
              </S.ParentLink>
            </S.Item>
          </React.Fragment>
        )}

        {/* Display the parent link, with overview or view all text, only if there are previous items and the current parent item has children */}
        {previousItems.length > 0 && currentParentItem?.url && (
          <S.Item key={currentParentItem.id}>
            <S.Link href={currentParentItem.url} target={currentParentItem.target}>
              <span aria-label={currentParentItem.text} data-cta-text>
                {translations['viewall']}
              </span>
            </S.Link>
          </S.Item>
        )}

        {/* Markup for the navigation items, include the right arrow for items with children */}
        {currentItemList.map((item) => {
          const isParentSystem = currentParentItem?.type === DropdownType.System && (item.children?.length ?? 0) > 0;
          const systemAttributes = isParentSystem ? { 'data-flex-item': true } : {};

          return (
            <S.Item key={item.id} {...systemAttributes}>
              <S.Link
                href={item.url}
                target={item.target}
                onClick={(event) => {
                  if (item.children?.length) {
                    event.preventDefault();
                    setPreviousItems([...previousItems, item]);
                  }
                }}
              >
                {isParentSystem && (
                  <S.Logo
                    style={
                      {
                        '--systemLogoHeight': `${getSystemLogoHeight(item.systemLogo) * 0.7}px`,
                      } as React.CSSProperties
                    }
                  >
                    {renderSystemLogo(item.systemLogo)}
                  </S.Logo>
                )}
                <span data-new={item.isNew} data-cta-text>
                  {item.text} {item.isNew && <S.New>{translations['custom.new']}</S.New>}
                </span>
                {!!item.children?.length && <S.RightArrow />}
              </S.Link>
            </S.Item>
          );
        })}
      </S.ItemList>

      {/* Display the supporting links, only on the first level */}
      {links && previousItems.length === 0 && (
        <S.LinksWrapper>
          {links.map((link, i) => (
            <S.Item key={`link-${i}: ${link.text}`}>
              <S.Link href={link.url} target={link.target ?? '_self'}>
                {link.text}
              </S.Link>
            </S.Item>
          ))}
        </S.LinksWrapper>
      )}
    </S.Container>
  );

  function renderParentLink(): React.ReactNode {
    // If there is no parent item, return null
    if (!currentParentItem) {
      return null;
    }

    // If the parent item has a system logo and a title, use both as the parent item
    if (currentParentItem.url && currentParentItem.systemLogo && currentParentItem.title) {
      return (
        <>
          {renderSystemLogo(currentParentItem.systemLogo)}
          <span>{currentParentItem.title}</span>
        </>
      );
    }

    // If the parent item has a system logo, use it as the parent item
    if (currentParentItem.url && currentParentItem.systemLogo) {
      return renderSystemLogo(currentParentItem.systemLogo);
    }

    // If the parent item has a title, use it as the parent item
    if (currentParentItem.url && currentParentItem.title) {
      return <span>{currentParentItem.title}</span>;
    }

    // If the parent item has a URL, use it as the parent link
    if (currentParentItem.url) {
      return <a href={currentParentItem.url}>{currentParentItem.text}</a>;
    }

    // Otherwise, just use the parent item text
    return <span>{currentParentItem.text}</span>;
  }
};

export default NavigationMobile;
